import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { bufferTime, EMPTY, exhaustMap, filter, mergeMap } from 'rxjs';
import { catchError, map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { WorkedTimesEntity, WorkedTimesEntityFromBackend } from '../../entities/worked-times.entity';
import { HttpService } from '../../services/http.service';
import { DaveActions } from '../actions/actions';
import { BaseActionTypes } from '../actions/base.actions';
import { State } from '../index';
import { getToken } from '../selectors/base.selectors';
import { WorkedTimesActionTypes } from '../actions/worked-times.actions';
import { selectWorkedTimesLoadPlan } from '../selectors/worked-times.selectors';

enum ErrorCodes {
    Load = 'Zeitstatistiken Abrufen fehlgeschlagen',
}
@Injectable()
export class WorkedTimesEffects {
    constructor(private actions$: Actions<DaveActions>, private store$: Store<State>, private gatewayHttpService: HttpService) {}

    LoadWorkedTimesFiltred$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(WorkedTimesActionTypes.LoadFiltered),
                withLatestFrom(this.store$.select(getToken)),
                mergeMap(([{ commissionIds, workDayIds }, token]) => {

                    return this.store$.select(selectWorkedTimesLoadPlan(commissionIds, workDayIds)).pipe(
                        take(1),
                        switchMap((plan) => {
                            const allNew = [...plan.newCommissionIds, ...plan.newWorkDayIds];
                            const allKnown = [...plan.knownCommissionIds, ...plan.knownWorkDayIds];

                            if (!allNew.length && !allKnown.length) {
                                this.store$.dispatch(
                                    WorkedTimesActionTypes.LoadByIdsSuccess({
                                        Payload: [],
                                        commissionIds,
                                        workDayIds,
                                        serverTimestamp: new Date().toISOString(),
                                    }),
                                );
                                return EMPTY;
                            }

                            const fields = WorkedTimesEntity.GQLFields.join(',');
                            const hasNew = plan.newCommissionIds.length > 0 || plan.newWorkDayIds.length > 0;
                            const hasKnown = (plan.knownCommissionIds.length > 0 || plan.knownWorkDayIds.length > 0) && !!plan.updatedSince;

                            let queryBody = '';
                            if (hasNew && hasKnown) {
                                queryBody = `
                                    new: workedtimes(
                                        commissionIds: [${plan.newCommissionIds.join(',')}],
                                        workDayIds: [${plan.newWorkDayIds.join(',')}]
                                    ) { ${fields} }
                                    known: workedtimes(
                                        commissionIds: [${plan.knownCommissionIds.join(',')}],
                                        workDayIds: [${plan.knownWorkDayIds.join(',')}],
                                        updatedSince: "${plan.updatedSince}",
                                        withDeleted: true
                                    ) { ${fields} }
                                `;
                            } else {
                                const cIds = [...plan.newCommissionIds, ...plan.knownCommissionIds];
                                const wIds = [...plan.newWorkDayIds, ...plan.knownWorkDayIds];
                                queryBody = `
                                    workedtimes(
                                        commissionIds: [${cIds.join(',')}],
                                        workDayIds: [${wIds.join(',')}]
                                        ${plan.updatedSince ? `, updatedSince: "${plan.updatedSince}", withDeleted: true` : ''}
                                    ) { ${fields} }
                                `;
                            }

                            const queryString = `query { ${queryBody} }`;

                            return this.gatewayHttpService.graphQl({ query: queryString }, { token, retry: true }).pipe(
                                tap((res: any) => {
                                    if (!res || (!res.workedtimes && !res.new && !res.known)) {
                                        this.store$.dispatch(
                                            BaseActionTypes.ErrorAction({ Payload: { ToasterMessage: ErrorCodes.Load } }),
                                        );
                                        return;
                                    }

                                    const rawEntities: any[] = [
                                        ...(res.workedtimes || []),
                                        ...(res.new || []),
                                        ...(res.known || [])
                                    ];

                                    const entities = rawEntities.map((val) => WorkedTimesEntityFromBackend(val));
                                    const serverTimestamp = new Date().toISOString();

                                    this.store$.dispatch(
                                        WorkedTimesActionTypes.LoadByIdsSuccess({
                                            Payload: entities,
                                            commissionIds,
                                            workDayIds,
                                            serverTimestamp,
                                        }),
                                    );

                                    const del = rawEntities.filter((w) => w.deletedAt);
                                    if (del.length) {
                                        this.store$.dispatch(WorkedTimesActionTypes.RemoveMany({ Payload: del.map((w) => w.id) }));
                                    }
                                }),
                                catchError((err, caught) => {
                                    this.store$.dispatch(
                                        WorkedTimesActionTypes.LoadByIdsFailure({ commissionIds, workDayIds, error: err }),
                                    );
                                    this.store$.dispatch(
                                        BaseActionTypes.ErrorAction({
                                            Payload: { ToasterMessage: ErrorCodes.Load, Err: err, Caught: caught },
                                        }),
                                    );
                                    return EMPTY;
                                }),
                            );
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );


GetAllWorkedTimesQuery$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(WorkedTimesActionTypes.Load),
                withLatestFrom(this.store$.select(getToken)),
                exhaustMap(([{ Payload }, token]) => {
                    const queryString = `query {
          workedtimes${Payload?.updatedSince ? `(updatedSince: "${Payload.updatedSince}", withDeleted: true)` : ''} {
            ${WorkedTimesEntity.GQLFields.join(',')}
          }
        }
        `;
                    return this.gatewayHttpService.graphQl({ query: queryString }, { token, retry: true }).pipe(
                        tap((res) => {
                            if (!(Payload?.updatedSince && !res.workedtimes.length)) {
                                this.store$.dispatch(
                                    res && res.workedtimes
                                        ? Payload?.updatedSince
                                            ? WorkedTimesActionTypes.UpdateMany({
                                                  Payload: res.workedtimes.map((val) => WorkedTimesEntityFromBackend(val)),
                                                  updateLatestUpdatedAt: true,
                                              })
                                            : WorkedTimesActionTypes.UpdateAll({
                                                  Payload: res.workedtimes.map((val) => WorkedTimesEntityFromBackend(val)),
                                                  updateLatestUpdatedAt: true,
                                              })
                                        : BaseActionTypes.ErrorAction({
                                              Payload: { ToasterMessage: ErrorCodes.Load },
                                          }),
                                );
                                const del = res?.workedtimes.filter(w => w.deletedAt);
                                if (del?.length) {
                                    this.store$.dispatch(WorkedTimesActionTypes.RemoveMany({Payload: del.map(w => w.id)}))
                                }
                            }
                        }),
                        catchError((err, caught) => {
                            this.store$.dispatch(
                                BaseActionTypes.ErrorAction({
                                    Payload: {
                                        ToasterMessage: ErrorCodes.Load,
                                        Err: err,
                                        Caught: caught,
                                    },
                                }),
                            );
                            return EMPTY;
                        }),
                    );
                }),
            ),
        { dispatch: false },
    );
}
